import React, { useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Chip, Checkbox,
  ToggleButton, ToggleButtonGroup, Popover, IconButton,
  Select, Menu, MenuItem, FormControl, Pagination, Button, Divider, ListItemText,
  Drawer, useMediaQuery, Collapse,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import PaidIcon from '@mui/icons-material/Paid';
import PlaceIcon from '@mui/icons-material/Place';
import ParkIcon from '@mui/icons-material/Park';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HouseIcon from '@mui/icons-material/House';
import TrainIcon from '@mui/icons-material/Train';
import SchoolIcon from '@mui/icons-material/School';
import DomainIcon from '@mui/icons-material/Domain';
import BlockIcon from '@mui/icons-material/Block';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { PsfColors } from '../theme';
import {
  fetchProjects, exportPsfPdf, ProjectSummary, PropertyTypeFilter, NewLaunchUnitFilters,
} from '../services/api';
import {
  AdjustmentState, TenureChoice, AgeBandAdjust, AmenityPremium, defaultAdjustmentState, normalizeAdjustmentState,
  computePersonalizedPsf, projectAgeInfo,
} from '../lib/adjustments';
import AdjustmentPanel from './AdjustmentPanel';
import NewLaunchDialog, { PsfExplain } from './NewLaunchDialog';
import { HeaderMenu, HeaderRange, MultiSelectFilter } from './CompareView';
import ReferenceRadiusFilter, { ReferenceRadiusValue } from './ReferenceRadiusFilter';
import { recordFilterApplication } from '../services/api';
import { serializePsfFilterHistory } from '../services/filterHistory';
import {
  QuestCard, PowerMeter, PillSelect, ToggleChip, RangeStat, RangePreset, RangeValue, rangeIsSet,
} from './QuestKit';
import BenchmarkJourney, { ChoiceTile, JourneyApi, JourneyStep } from './journey/BenchmarkJourney';
import { useGlobalCalculatorSettings } from '../../super-calculator/context/GlobalCalculatorSettingsContext';

type Period = '1Y' | '2Y' | '5Y';

const COMPACT_VIEWPORT_QUERY = '(max-width:1279px), (max-height:950px)';

const fmt = (n: number | null | undefined, prefix = '') => (n == null ? '—' : prefix + Number(n).toLocaleString());
const fmtDistance = (n: number | null | undefined) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('en-SG'));
const formatTenure = (value: string | null | undefined): string => {
  if (!value) return '—';
  if (/freehold/i.test(value)) return 'Freehold';
  return value.match(/\d{2,3}/)?.[0] || value;
};
// Reserved for the temporarily disabled AA/RY + AA column formatters.
// const fmtPct = (n: number | null | undefined) => n == null ? '—' : `${Number(n).toFixed(2)}%`;
const rangeText = (min?: string, max?: string) => (min && max ? `${min} – ${max}` : min ? `≥ ${min}` : `≤ ${max}`);
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// Avg Price / Avg Sqft lead: they are the most-used criteria. This order
// also drives the applied-filter chips and the PDF filter summary.
const RANGE_FIELDS = [
  { key: 'avgPrice', label: 'Avg Price ($)', money: true },
  { key: 'avgAreaSqft', label: 'Avg Sqft', money: false },
  { key: 'avgPsf', label: 'Avg PSF ($)', money: true },
  { key: 'nearestMrtDistance', label: 'MRT Distance (m)', money: false },
  { key: 'nearestPriSchDistance', label: 'Pri Sch Distance (m)', money: false },
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc (km)', money: false },
  { key: 'age', label: 'Age', money: false },
  { key: 'remainingLease', label: 'Lease Left', money: false },
  { key: 'completionYear', label: 'Completed', money: false },
  { key: 'unitCount', label: 'No. Of Units', money: false },
  { key: 'txnCount', label: 'No. of Tsn', money: false },
] as const;
type RangeFieldKey = typeof RANGE_FIELDS[number]['key'];

const BUDGET_KEYS: RangeFieldKey[] = ['avgPrice', 'avgAreaSqft', 'avgPsf'];
const BUILDING_KEYS: RangeFieldKey[] = ['age', 'remainingLease', 'completionYear', 'unitCount', 'txnCount'];

const RANGE_PRESETS: Partial<Record<RangeFieldKey, RangePreset[]>> = {
  avgPrice: [
    { label: '< $1M', max: '1,000,000' },
    { label: '$1–1.5M', min: '1,000,000', max: '1,500,000' },
    { label: '$1.5–2M', min: '1,500,000', max: '2,000,000' },
    { label: '$2–3M', min: '2,000,000', max: '3,000,000' },
    { label: '$3M+', min: '3,000,000' },
  ],
  avgAreaSqft: [
    { label: '< 600', max: '600' },
    { label: '600–900', min: '600', max: '900' },
    { label: '900–1,200', min: '900', max: '1200' },
    { label: '1,200+', min: '1200' },
  ],
  avgPsf: [
    { label: '< $1.5k', max: '1,500' },
    { label: '$1.5–2k', min: '1,500', max: '2,000' },
    { label: '$2–2.5k', min: '2,000', max: '2,500' },
    { label: '$2.5k+', min: '2,500' },
  ],
  nearestMrtDistance: [
    { label: '≤ 300m', max: '300' },
    { label: '≤ 500m', max: '500' },
    { label: '≤ 1km', max: '1000' },
  ],
  nearestPriSchDistance: [
    { label: '≤ 1km', max: '1000' },
    { label: '≤ 2km', max: '2000' },
  ],
  age: [
    { label: '≤ 5 yrs', max: '5' },
    { label: '≤ 10 yrs', max: '10' },
    { label: '≤ 20 yrs', max: '20' },
  ],
};

const QUEST_ACCENTS = {
  tenure: '#5cc8ff',
  market: '#2196f3',
  budget: '#f47920',
  location: '#16a085',
  amenities: '#e5a50a',
  building: '#7e57c2',
} as const;

const parseRangeNumber = (value: string): number => parseFloat(value.replace(/,/g, ''));

// Classic: every criterion on one panel + Apply. Guided: the scroll journey.
// The choice is remembered per browser.
type QueryMode = 'classic' | 'guided';
const QUERY_MODE_KEY = 'psf.benchmark.queryMode';
const readQueryMode = (): QueryMode => {
  try { return window.localStorage.getItem(QUERY_MODE_KEY) === 'classic' ? 'classic' : 'guided'; } catch { return 'guided'; }
};

function distinctSorted(values: unknown[]): string[] {
  return Array.from(new Set(
    values
      .filter((value) => value != null && String(value).trim() !== '')
      .map((value) => String(value).trim()),
  )).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
}

const tenureSortValue = (value: unknown): number => {
  const text = String(value ?? '').trim();
  if (/freehold/i.test(text)) return Number.POSITIVE_INFINITY;
  const match = text.match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};
const tenureGroupForValue = (value: unknown): 'leasehold' | 'freehold' | null => {
  const text = String(value ?? '').trim();
  if (/freehold/i.test(text)) return 'freehold';
  const years = Number(text.match(/\d+(?:\.\d+)?/)?.[0]);
  return Number.isFinite(years) ? (years <= 103 ? 'leasehold' : 'freehold') : null;
};

interface ActiveFilter {
  id: string;
  label: string;
  /** Present only for filters that can be dropped straight from the chip strip. */
  onRemove?: () => void;
}

interface BenchmarkViewProps {
  c: PsfColors;
  tenureChoice: TenureChoice;
  onReset: () => void;
  reportContext?: { userInfo: { Name?: string; Mobile?: string; Email?: string; CEANumber?: string; Photo?: string } | null; consumerName?: string | null };
}

interface BenchRow {
  project: ProjectSummary;
  age: number | null;
  ageEstimated: boolean;
  remainingLease: number | null;
  tenureAdj: number;
  physicalAdj: number;
  amenitiesAdj: number;
  personalizedPsf: number | null;
  depPct: number | null;
  band: AgeBandAdjust | null;
  bandAmount: number;
  extrasApplied: Array<{ label: string; amount: number; value?: number; mode?: 'pct' | 'psf' }>;
  mrtPremium: AmenityPremium | null;
  mrtPremiumAmount: number;
  priSchPremium: AmenityPremium | null;
  priSchPremiumAmount: number;
}

type SortKey = 'projectName' | 'district' | 'tenure' | 'age' | 'remainingLease'
  | 'region' | 'subtype' | 'completionYear' | 'nearestPriSch' | 'nearestPriSchDistance'
  | 'nearestMrt' | 'nearestMrtDistance' | 'unitCount' | 'txnCount' | 'avgAreaSqft'
  | 'avgPrice' | 'minPsf' | 'maxPsf' | 'avgPsf'
  | 'distanceFromReferenceKm' | 'adj' | 'personalizedPsf';

type BenchmarkColumn = {
  key: SortKey;
  label: string;
  headerLines?: string[];
  numeric: boolean;
  filterType: 'multi' | 'range';
  width: number;
  filterDisabled?: boolean;
};

const BENCHMARK_COLUMNS: BenchmarkColumn[] = [
  { key: 'projectName', label: 'Project', numeric: false, filterType: 'multi', width: 180 },
  { key: 'district', label: 'District', numeric: true, filterType: 'multi', width: 90 },
  { key: 'region', label: 'Region', numeric: false, filterType: 'multi', width: 90 },
  { key: 'subtype', label: 'Type', numeric: false, filterType: 'multi', width: 100 },
  { key: 'tenure', label: 'Tenure', numeric: false, filterType: 'multi', width: 120 },
  { key: 'age', label: 'Age', numeric: true, filterType: 'range', width: 72 },
  { key: 'remainingLease', label: 'Lease Left', headerLines: ['Lease', 'Left'], numeric: true, filterType: 'range', width: 100 },
  { key: 'completionYear', label: 'Completed', numeric: true, filterType: 'range', width: 95 },
  { key: 'nearestPriSch', label: 'Nearest Pri Sch', numeric: false, filterType: 'multi', width: 170 },
  { key: 'nearestPriSchDistance', label: 'Pri Sch Dist (m)', headerLines: ['Pri Sch', 'Dist (m)'], numeric: true, filterType: 'range', width: 145 },
  { key: 'nearestMrt', label: 'Nearest MRT', numeric: false, filterType: 'multi', width: 150 },
  { key: 'nearestMrtDistance', label: 'MRT Dist (m)', headerLines: ['MRT Dist', '(m)'], numeric: true, filterType: 'range', width: 130 },
  { key: 'unitCount', label: 'No. Of Units', headerLines: ['No. Of', 'Units'], numeric: true, filterType: 'range', width: 105 },
  { key: 'txnCount', label: 'No. of Tsn', headerLines: ['No. of', 'Tsn'], numeric: true, filterType: 'range', width: 95 },
  { key: 'avgAreaSqft', label: 'Avg Sqft', numeric: true, filterType: 'range', width: 95 },
  { key: 'avgPrice', label: 'Avg Price', numeric: true, filterType: 'range', width: 110 },
  // Temporarily disabled for the UI; retain the backend fields/calculations
  // so these columns can be reinstated without changing the API contract.
  // { key: 'aa', label: 'AA', numeric: true, filterType: 'range', filterDisabled: true, width: 72 },
  // { key: 'ryPlusAa', label: 'RY + AA', numeric: true, filterType: 'range', filterDisabled: true, width: 95 },
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc (km)', numeric: true, filterType: 'range', width: 130 },
  { key: 'minPsf', label: 'Min PSF', numeric: true, filterType: 'range', width: 95 },
  { key: 'maxPsf', label: 'Max PSF', numeric: true, filterType: 'range', width: 95 },
  { key: 'avgPsf', label: 'Avg PSF', numeric: true, filterType: 'range', width: 100 },
  { key: 'adj', label: '+ Adj', numeric: true, filterType: 'range', width: 85 },
  { key: 'personalizedPsf', label: 'Personalized PSF', numeric: true, filterType: 'range', width: 150 },
];

export default function BenchmarkView({ c, tenureChoice, onReset, reportContext }: BenchmarkViewProps) {
  const C = c;
  const { settings: { fontScalePt } } = useGlobalCalculatorSettings();
  const nowYear = new Date().getFullYear();
  const isMobile = useMediaQuery('(max-width:899px)');
  // On tablets and short laptop screens the criteria panel eats the whole
  // viewport, leaving four or five rows visible. The panel always opens on
  // arrival, but there it folds away once filters are applied and the table
  // renders at a tighter row density.
  const isCompactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [queryMode, setQueryMode] = useState<QueryMode>(readQueryMode);
  const changeQueryMode = (mode: QueryMode) => {
    setQueryMode(mode);
    try { window.localStorage.setItem(QUERY_MODE_KEY, mode); } catch { /* storage unavailable */ }
  };
  const [activeTenure, setActiveTenure] = useState<TenureChoice>(tenureChoice);

  const [period, setPeriod] = useState<Period>('1Y');
  const [propertyType, setPropertyType] = useState<PropertyTypeFilter>('condo');
  const [includeNewSale, setIncludeNewSale] = useState(false);
  const [rangeInputs, setRangeInputs] = useState<Record<string, string>>({});
  const [appliedRanges, setAppliedRanges] = useState<Record<string, string>>({});
  const [searchMulti, setSearchMulti] = useState({
    project: [] as string[],
    district: [] as string[],
    region: [] as string[],
    subtype: [] as string[],
    nearestPriSch: [] as string[],
    nearestMrt: [] as string[],
  });
  const [excludeExecutiveCondominiums, setExcludeExecutiveCondominiums] = useState(false);
  const [referenceRadius, setReferenceRadius] = useState<ReferenceRadiusValue | null>(null);
  const [referenceResetKey, setReferenceResetKey] = useState(0);
  const [appliedPeriod, setAppliedPeriod] = useState<Period>('1Y');
  const [appliedTenure, setAppliedTenure] = useState<TenureChoice>(tenureChoice);
  const [appliedPropertyType, setAppliedPropertyType] = useState<PropertyTypeFilter>('condo');
  const [appliedIncludeNewSale, setAppliedIncludeNewSale] = useState(false);
  const [appliedExcludeExecutiveCondominiums, setAppliedExcludeExecutiveCondominiums] = useState(false);
  const [appliedSearchMulti, setAppliedSearchMulti] = useState(searchMulti);
  const [appliedReferenceRadius, setAppliedReferenceRadius] = useState<ReferenceRadiusValue | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [newSaleHelpAnchor, setNewSaleHelpAnchor] = useState<HTMLElement | null>(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<Set<SortKey>>(
    () => new Set(BENCHMARK_COLUMNS.map((column) => column.key)),
  );
  const draftHistory = useMemo(() => serializePsfFilterHistory({
    period,
    tenure: activeTenure,
    propertyType: propertyType === 'condo' ? '' : propertyType,
    saleTypes: includeNewSale ? ['New Sale'] : [],
    excludeExecutiveCondominiums,
    projects: searchMulti.project,
    districts: searchMulti.district,
    regions: searchMulti.region,
    subtypes: searchMulti.subtype,
    nearestPriSch: searchMulti.nearestPriSch,
    nearestMrt: searchMulti.nearestMrt,
    reference: referenceRadius,
    ranges: rangeInputs,
  }), [period, activeTenure, propertyType, includeNewSale, excludeExecutiveCondominiums, searchMulti, referenceRadius, rangeInputs]);

  // Journey answers apply live: the draft is committed a beat after the last
  // edit, so the match count and the city backdrop narrow as the user goes.
  const commitDraft = useCallback(() => {
    setAppliedPeriod(period);
    setAppliedTenure(activeTenure);
    setAppliedPropertyType(propertyType);
    setAppliedIncludeNewSale(includeNewSale);
    setAppliedExcludeExecutiveCondominiums(excludeExecutiveCondominiums);
    setAppliedSearchMulti(searchMulti);
    setAppliedReferenceRadius(referenceRadius);
    setAppliedRanges(rangeInputs);
    setPage(1);
  }, [period, activeTenure, propertyType, includeNewSale, excludeExecutiveCondominiums, searchMulti, referenceRadius, rangeInputs]);

  useEffect(() => {
    if (queryMode !== 'guided') return undefined;
    const timer = window.setTimeout(commitDraft, 350);
    return () => window.clearTimeout(timer);
  }, [commitDraft, queryMode]);

  // Guided records history once per distinct set of answers, when the results
  // are revealed — not on every live commit. Classic records on every Apply.
  const lastRecordedHistory = useRef<string | null>(null);
  const recordJourney = useCallback(() => {
    const key = JSON.stringify(draftHistory);
    if (key === lastRecordedHistory.current) return;
    lastRecordedHistory.current = key;
    void recordFilterApplication('BENCHMARK_NEW_LAUNCH', draftHistory).catch(() => undefined);
  }, [draftHistory]);

  const applyFilters = useCallback(() => {
    commitDraft();
    setAppliedHeaderMulti({});
    setAppliedHeaderRanges({});
    setHeaderMulti({});
    setHeaderRanges({});
    // Hand the viewport back to the table as soon as the criteria are in.
    if (isCompactViewport) setFiltersOpen(false);
    lastRecordedHistory.current = JSON.stringify(draftHistory);
    void recordFilterApplication('BENCHMARK_NEW_LAUNCH', draftHistory).catch(() => undefined);
  }, [commitDraft, draftHistory, isCompactViewport]);

  const clearFilters = useCallback(() => {
    const emptySearch = { project: [], district: [], region: [], subtype: [], nearestPriSch: [], nearestMrt: [] };
    setPeriod('1Y');
    setActiveTenure(tenureChoice);
    setPropertyType('condo');
    setIncludeNewSale(false);
    setExcludeExecutiveCondominiums(false);
    setSearchMulti(emptySearch);
    setRangeInputs({});
    setHeaderMulti({});
    setHeaderRanges({});
    setReferenceResetKey((key) => key + 1);
  }, [tenureChoice]);

  // `clearFilters` only resets the draft inputs. The chip strip shows what the
  // table is actually filtered by, so its reset clears the applied state too.
  const clearAllFilters = useCallback(() => {
    clearFilters();
    setAppliedPeriod('1Y');
    setAppliedTenure(tenureChoice);
    setAppliedPropertyType('condo');
    setAppliedIncludeNewSale(false);
    setAppliedExcludeExecutiveCondominiums(false);
    setAppliedSearchMulti({ project: [], district: [], region: [], subtype: [], nearestPriSch: [], nearestMrt: [] });
    setAppliedRanges({});
    setAppliedReferenceRadius(null);
    setAppliedHeaderMulti({});
    setAppliedHeaderRanges({});
    setPage(1);
  }, [clearFilters, tenureChoice]);

  const handleReferenceRadiusChange = useCallback((value: ReferenceRadiusValue | null) => {
    setReferenceRadius(value);
  }, []);

  const activeRangeCount = useMemo(
    () => RANGE_FIELDS.filter((f) => rangeInputs[`${f.key}Min`] || rangeInputs[`${f.key}Max`]).length,
    [rangeInputs],
  );

  // Bedroom/size carry over to the new-launch unit matching so the dialog
  // compares like-for-like (psf/price ranges only scope the resale table —
  // the unit threshold is the personalized PSF itself).
  const unitFilters = useMemo((): NewLaunchUnitFilters => ({
    excludeExecutiveCondominiums: appliedExcludeExecutiveCondominiums,
  }), [appliedExcludeExecutiveCondominiums]);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  const [adjust, setAdjust] = useState<AdjustmentState>(() => normalizeAdjustmentState(defaultAdjustmentState(activeTenure), activeTenure));
  // Curve/slider edits can fire many updates per second. Keep the controls
  // responsive while allowing the expensive project calculations to settle.
  const calculationAdjust = useDeferredValue(adjust);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(false);

  const [sortBy, setSortBy] = useState<SortKey>('txnCount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [sortMode, setSortMode] = useState<'default' | 'explicit'>('default');
  const [headerMulti, setHeaderMulti] = useState<Partial<Record<SortKey, string[]>>>({});
  const [headerRanges, setHeaderRanges] = useState<Partial<Record<SortKey, HeaderRange>>>({});
  const [appliedHeaderMulti, setAppliedHeaderMulti] = useState<Partial<Record<SortKey, string[]>>>({});
  const [appliedHeaderRanges, setAppliedHeaderRanges] = useState<Partial<Record<SortKey, HeaderRange>>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [dialog, setDialog] = useState<{
    open: boolean;
    psf: number | null;
    projectName: string | null;
    explain: PsfExplain | null;
    unitFilters: NewLaunchUnitFilters | null;
  }>({ open: false, psf: null, projectName: null, explain: null, unitFilters: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProjects({
        period: appliedPeriod,
        propertyType: appliedPropertyType,
        tenureGroup: appliedTenure,
        // Benchmark compares the RESALE market against new launches, so new
        // sales are excluded unless the user opts in.
        saleTypes: appliedIncludeNewSale ? [] : ['Resale', 'Sub Sale'],
        excludeExecutiveCondominiums: appliedExcludeExecutiveCondominiums,
        ...(appliedReferenceRadius ? {
          referenceLatitude: appliedReferenceRadius.latitude,
          referenceLongitude: appliedReferenceRadius.longitude,
          radiusKm: appliedReferenceRadius.radiusKm,
        } : {}),
        limit: 3000,
      });
      setProjects(res.data);
      setDateRange({ start: res.startDate, end: res.endDate });
    } catch (e: any) {
      setError(e.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod, appliedPropertyType, appliedTenure, appliedIncludeNewSale, appliedExcludeExecutiveCondominiums, appliedReferenceRadius]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Compute rows ─────────────────────────────────────────────────────────
  const rows: BenchRow[] = useMemo(() => projects.map((p) => {
    const info = projectAgeInfo(p, nowYear);
    const bd = computePersonalizedPsf(p, calculationAdjust, nowYear);
    return {
      project: p,
      age: info.age,
      ageEstimated: info.estimated,
      remainingLease: info.remainingLease,
      tenureAdj: bd.tenureAdj,
      physicalAdj: bd.physicalAdj,
      amenitiesAdj: bd.amenitiesAdj,
      personalizedPsf: bd.personalizedPsf,
      depPct: bd.depPct,
      band: bd.band,
      bandAmount: bd.bandAmount,
      extrasApplied: bd.extrasApplied,
      mrtPremium: bd.mrtPremium,
      mrtPremiumAmount: bd.mrtPremiumAmount,
      priSchPremium: bd.priSchPremium,
      priSchPremiumAmount: bd.priSchPremiumAmount,
    };
  }), [projects, calculationAdjust, nowYear]);

  const methodLabel = useMemo(() => {
    if (adjust.curveEdited) return 'Custom curve';
    if (adjust.tenureChoice === 'leasehold') {
      return `Linear (${adjust.linearTermYears}y term)`;
    }
    return adjust.freeholdMethod === 'landBuilding'
      ? `Land ${adjust.landSharePct}% + building (${adjust.buildingLifeYears}y life)`
      : `Market discount ${adjust.marketRatePct}%/yr`;
  }, [adjust]);

  const openPsfDialog = (r: BenchRow) => {
    if (r.personalizedPsf == null || r.project.avgPsf == null) return;
    const explain: PsfExplain = {
        projectName: r.project.projectName,
        basePsf: r.project.avgPsf,
        age: r.age,
        ageEstimated: r.ageEstimated,
        remainingLease: r.remainingLease,
        level: adjust.level,
        methodLabel,
        depPct: r.depPct,
        tenureAdj: r.tenureAdj,
        band: r.band,
        bandAmount: r.bandAmount,
        extrasApplied: r.extrasApplied,
        physicalAdj: r.physicalAdj,
        amenitiesAdj: r.amenitiesAdj,
        mrtPremium: r.mrtPremium,
        mrtPremiumAmount: r.mrtPremiumAmount,
        priSchPremium: r.priSchPremium,
        priSchPremiumAmount: r.priSchPremiumAmount,
        mrtPremiumSetting: adjust.mrtPremium,
        priSchPremiumSetting: adjust.priSchPremium,
        nearestMrtDistance: r.project.nearestMrtDistance,
        nearestPriSchDistance: r.project.nearestPriSchDistance,
        personalizedPsf: r.personalizedPsf,
    };
    // Preserve the original workflow: the first click opens the unit/results
    // dialog, whose individual calculation values open focused detail dialogs.
    setDialog({
      open: true,
      psf: r.personalizedPsf,
      projectName: r.project.projectName,
      explain,
      unitFilters,
    });
  };

  const sortValue = (r: BenchRow, key: SortKey): string | number | null => {
    switch (key) {
      case 'projectName': return r.project.projectName;
      case 'district': return r.project.district;
      case 'region': return r.project.region;
      case 'subtype': return r.project.subtype;
      case 'tenure': return r.project.tenure;
      case 'age': return r.age;
      case 'remainingLease': return r.remainingLease;
      case 'completionYear': return r.project.completionYear;
      case 'nearestPriSch': return r.project.nearestPriSch;
      case 'nearestPriSchDistance': return r.project.nearestPriSchDistance;
      case 'nearestMrt': return r.project.nearestMrt;
      case 'nearestMrtDistance': return r.project.nearestMrtDistance;
      case 'unitCount': return r.project.unitCount;
      case 'txnCount': return r.project.txnCount;
      case 'avgAreaSqft': return r.project.avgAreaSqft;
      case 'avgPrice': return r.project.avgPrice;
      // AA/RY + AA sorting is temporarily disabled with the columns.
      case 'minPsf': return r.project.minPsf;
      case 'maxPsf': return r.project.maxPsf;
      case 'avgPsf': return r.project.avgPsf;
      case 'distanceFromReferenceKm': return r.project.distanceFromReferenceKm;
      case 'adj': return r.tenureAdj + r.physicalAdj + r.amenitiesAdj;
      case 'personalizedPsf': return r.personalizedPsf;
      default: return null;
    }
  };

  const searchOptions = useMemo(() => {
    return {
      project: distinctSorted(rows.map((r) => r.project.projectName)),
      district: distinctSorted(rows.map((r) => r.project.district)),
      region: distinctSorted(rows.map((r) => r.project.region)),
      subtype: distinctSorted(rows.map((r) => r.project.subtype)),
      nearestPriSch: distinctSorted(rows.map((r) => r.project.nearestPriSch)),
      nearestMrt: distinctSorted(rows.map((r) => r.project.nearestMrt)),
    };
  }, [rows]);

  const searchedRows = useMemo(() => rows.filter((r) => {
    if (appliedExcludeExecutiveCondominiums && /executive\s+condominium|\bec\b/i.test(r.project.subtype || '')) return false;
    if (appliedSearchMulti.project.length && !appliedSearchMulti.project.includes(r.project.projectName)) return false;
    if (appliedSearchMulti.district.length && !appliedSearchMulti.district.includes(String(r.project.district ?? ''))) return false;
    if (appliedSearchMulti.region.length && !appliedSearchMulti.region.includes(String(r.project.region ?? ''))) return false;
    if (appliedSearchMulti.subtype.length && !appliedSearchMulti.subtype.includes(String(r.project.subtype ?? ''))) return false;
    if (appliedSearchMulti.nearestPriSch.length && !appliedSearchMulti.nearestPriSch.includes(String(r.project.nearestPriSch ?? ''))) return false;
    if (appliedSearchMulti.nearestMrt.length && !appliedSearchMulti.nearestMrt.includes(String(r.project.nearestMrt ?? ''))) return false;
    for (const field of RANGE_FIELDS) {
      const value = sortValue(r, field.key);
      const min = parseRangeNumber(appliedRanges[`${field.key}Min`] || '');
      const max = parseRangeNumber(appliedRanges[`${field.key}Max`] || '');
      if (Number.isFinite(min) && (value == null || Number(value) < min)) return false;
      if (Number.isFinite(max) && (value == null || Number(value) > max)) return false;
    }
    return true;
  }), [rows, appliedExcludeExecutiveCondominiums, appliedSearchMulti, appliedRanges]);

  const availableColumns = appliedReferenceRadius
    ? BENCHMARK_COLUMNS
    : BENCHMARK_COLUMNS.filter((column) => column.key !== 'distanceFromReferenceKm');
  const visibleColumns = availableColumns.filter((col) => visibleColumnKeys.has(col.key));

  const visibleRows = useMemo(() => {
    let out = searchedRows.filter((r) => {
      for (const [key, values] of Object.entries(appliedHeaderMulti) as [SortKey, string[]][]) {
        if (!values?.length) continue;
        if (key === 'tenure') {
          const group = tenureGroupForValue(r.project.tenure);
          if (!group || !values.some((value) => value.toLowerCase().includes(group))) return false;
        } else if (!values.includes(String(sortValue(r, key) ?? ''))) return false;
      }
      for (const [key, range] of Object.entries(appliedHeaderRanges) as [SortKey, HeaderRange][]) {
        if (!range?.min && !range?.max) continue;
        const value = Number(sortValue(r, key));
        if (!Number.isFinite(value)) return false;
        if (range.min && value < parseRangeNumber(range.min)) return false;
        if (range.max && value > parseRangeNumber(range.max)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out = out.map((row, index) => ({ row, index })).sort((a, b) => {
      const av = sortValue(a.row, sortBy);
      const bv = sortValue(b.row, sortBy);
      if (av == null && bv == null) return a.index - b.index;
      if (av == null) return 1;
      if (bv == null) return -1;
      let comparison = 0;
      if (sortBy === 'tenure') comparison = tenureSortValue(av) - tenureSortValue(bv);
      else if (typeof av === 'number' && typeof bv === 'number') comparison = av - bv;
      else comparison = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      if (Number.isNaN(comparison)) comparison = 0;
      return comparison === 0 ? a.index - b.index : comparison * dir;
    }).map(({ row }) => row);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedRows, appliedHeaderMulti, appliedHeaderRanges, sortBy, sortDir]);

  // Level 4 charts reflect exactly what the table shows. Keyed on the project
  // list (not the adjusted rows) so slider drags don't rebuild it.
  const visibleProjectKey = visibleRows.map((row) => row.project.projectName).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const amenityProjects = useMemo(() => visibleRows.map((row) => row.project), [visibleProjectKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const matchedNames = useMemo(() => new Set(visibleRows.map((row) => row.project.projectName)), [visibleProjectKey]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const pagedRows = useMemo(
    () => visibleRows.slice((page - 1) * pageSize, page * pageSize),
    [visibleRows, page, pageSize],
  );
  const isLeasehold = activeTenure === 'leasehold';

  const displayValueFor = useCallback((row: BenchRow, key: SortKey): string => {
    switch (key) {
      case 'projectName': return row.project.projectName;
      case 'district': return row.project.district == null ? '—' : String(row.project.district);
      case 'region': return row.project.region || '—';
      case 'subtype': return row.project.subtype || '—';
      case 'tenure': return formatTenure(row.project.tenure);
      case 'age': return row.age == null ? '—' : `${row.age}${row.ageEstimated ? '*' : ''}`;
      case 'remainingLease': return isLeasehold ? (row.remainingLease == null ? '—' : String(row.remainingLease)) : 'Freehold';
      case 'completionYear': return row.project.completionYear == null ? '—' : String(row.project.completionYear);
      case 'nearestPriSch': return row.project.nearestPriSch || '—';
      case 'nearestPriSchDistance': return fmtDistance(row.project.nearestPriSchDistance);
      case 'nearestMrt': return row.project.nearestMrt || '—';
      case 'nearestMrtDistance': return fmtDistance(row.project.nearestMrtDistance);
      case 'unitCount': return row.project.unitCount == null ? '—' : row.project.unitCount.toLocaleString();
      case 'txnCount': return row.project.txnCount.toLocaleString();
      case 'avgAreaSqft': return fmt(row.project.avgAreaSqft);
      case 'avgPrice': return fmt(row.project.avgPrice, '$');
      case 'minPsf': return fmt(row.project.minPsf, '$');
      case 'maxPsf': return fmt(row.project.maxPsf, '$');
      case 'avgPsf': return fmt(row.project.avgPsf, '$');
      case 'distanceFromReferenceKm': return row.project.distanceFromReferenceKm == null ? '—' : row.project.distanceFromReferenceKm.toFixed(2);
      case 'adj': return adjustLabel(row.tenureAdj + row.physicalAdj + row.amenitiesAdj, adjust.level);
      case 'personalizedPsf': return fmt(row.personalizedPsf, '$');
      default: return '—';
    }
  }, [adjust.level, isLeasehold]);

  const tableColumnWidths = useMemo(() => {
    // Measure the same strings rendered by the table, including the filter
    // affordance in each header. This keeps columns compact while ensuring
    // no displayed header or result value wraps or gets clipped.
    const canvas = typeof document === 'undefined' ? null : document.createElement('canvas');
    const context = canvas?.getContext('2d') || null;
    const rootRemPx = 16 + fontScalePt;
    const headerFont = `700 ${((isCompactViewport ? 0.68 : 0.72) * rootRemPx).toFixed(2)}px Roboto, Arial, sans-serif`;
    const bodyFont = `400 ${(0.73 * rootRemPx).toFixed(2)}px Roboto, Arial, sans-serif`;
    const widths: Record<SortKey, number> = {} as Record<SortKey, number>;
    visibleColumns.forEach((column) => {
      const headerLines = column.headerLines || [column.label];
      const headerWidth = context ? (() => {
        context.font = headerFont;
        return Math.max(...headerLines.map((line) => context.measureText(line).width));
      })() : Math.max(...headerLines.map((line) => line.length * 7));
      let valueWidth = 0;
      if (context) context.font = bodyFont;
      visibleRows.forEach((row) => {
        const value = displayValueFor(row, column.key);
        valueWidth = Math.max(valueWidth, context ? context.measureText(value).width : value.length * 7);
      });
      // 16px horizontal cell padding + 22px for the sort/filter button and a
      // small safety margin. Numeric columns still get a readable minimum.
      widths[column.key] = Math.max(44, Math.ceil(Math.max(headerWidth, valueWidth) + 42 + (column.key === 'personalizedPsf' ? 20 : 0)));
    });
    return widths;
  }, [displayValueFor, fontScalePt, isCompactViewport, visibleColumns, visibleRows]);

  const summary = useMemo(() => {
    const base = visibleRows.map((r) => r.project.avgPsf).filter((v): v is number => v != null);
    const pers = visibleRows.map((r) => r.personalizedPsf).filter((v): v is number => v != null);
    return {
      avgBase: base.length ? Math.round(base.reduce((a, b) => a + b, 0) / base.length) : null,
      avgPersonalized: pers.length ? Math.round(pers.reduce((a, b) => a + b, 0) / pers.length) : null,
    };
  }, [visibleRows]);

  const handleSort = (key: SortKey, direction: 'default' | 'asc' | 'desc') => {
    if (direction === 'default') { setSortBy('txnCount'); setSortDir('desc'); setSortMode('default'); }
    else { setSortBy(key); setSortDir(direction); setSortMode('explicit'); }
    setPage(1);
  };

  // ── Applied filter summary ───────────────────────────────────────────────
  // The criteria panel collapses, so everything the visible rows were filtered
  // by is written out as chips above the table. The same list feeds the PDF.
  const activeFilters = useMemo((): ActiveFilter[] => {
    const columnLabels = new Map(BENCHMARK_COLUMNS.map((column) => [column.key, column.label] as const));
    const periodLabel = appliedPeriod === '1Y' ? '1 year' : appliedPeriod === '2Y' ? '2 years' : '5 years';
    const typeLabel = appliedPropertyType === 'condo' ? 'Condo / Apartment'
      : appliedPropertyType === 'landed' ? 'Landed' : 'All types';
    const out: ActiveFilter[] = [
      { id: 'period', label: `Period: ${periodLabel}` },
      { id: 'tenure', label: `Tenure: ${isLeasehold ? 'Leasehold (≤103 yrs)' : '999-yr / Freehold'}` },
      { id: 'propertyType', label: `Property type: ${typeLabel}` },
      {
        id: 'saleTypes',
        label: appliedIncludeNewSale ? 'Sale types: Resale, Sub Sale, New Sale' : 'Sale types: Resale, Sub Sale',
      },
    ];
    if (appliedExcludeExecutiveCondominiums) {
      out.push({
        id: 'excludeEc',
        label: 'E.C excluded',
        onRemove: () => {
          setExcludeExecutiveCondominiums(false);
          setAppliedExcludeExecutiveCondominiums(false);
          setPage(1);
        },
      });
    }
    if (appliedReferenceRadius) {
      out.push({
        id: 'reference',
        label: `Within ${appliedReferenceRadius.radiusKm} km of ${appliedReferenceRadius.label}`,
        onRemove: () => {
          setReferenceRadius(null);
          setAppliedReferenceRadius(null);
          setRangeInputs((previous) => {
            const next = { ...previous };
            delete next.distanceFromReferenceKmMin;
            delete next.distanceFromReferenceKmMax;
            return next;
          });
          setAppliedRanges((previous) => {
            const next = { ...previous };
            delete next.distanceFromReferenceKmMin;
            delete next.distanceFromReferenceKmMax;
            return next;
          });
          setReferenceResetKey((key) => key + 1);
          setPage(1);
        },
      });
    }
    const multiFields: Array<[keyof typeof appliedSearchMulti, string]> = [
      ['project', 'Projects'], ['district', 'Districts'], ['region', 'Regions'],
      ['subtype', 'Types'], ['nearestPriSch', 'Nearest Pri Sch'], ['nearestMrt', 'Nearest MRT'],
    ];
    multiFields.forEach(([key, label]) => {
      const values = appliedSearchMulti[key];
      if (!values.length) return;
      out.push({
        id: `multi-${key}`,
        label: `${label}: ${values.join(', ')}`,
        onRemove: () => {
          setSearchMulti((previous) => ({ ...previous, [key]: [] }));
          setAppliedSearchMulti((previous) => ({ ...previous, [key]: [] }));
          setPage(1);
        },
      });
    });
    RANGE_FIELDS.forEach((field) => {
      const min = appliedRanges[`${field.key}Min`];
      const max = appliedRanges[`${field.key}Max`];
      if (!min && !max) return;
      out.push({
        id: `range-${field.key}`,
        label: `${field.label}: ${rangeText(min, max)}`,
        onRemove: () => {
          const drop = (previous: Record<string, string>) => {
            const next = { ...previous };
            delete next[`${field.key}Min`];
            delete next[`${field.key}Max`];
            return next;
          };
          setRangeInputs(drop);
          setAppliedRanges(drop);
          setPage(1);
        },
      });
    });
    (Object.entries(appliedHeaderMulti) as Array<[SortKey, string[] | undefined]>).forEach(([key, values]) => {
      if (!values?.length) return;
      out.push({
        id: `column-multi-${key}`,
        label: `${columnLabels.get(key) || key}: ${values.join(', ')}`,
        onRemove: () => {
          setHeaderMulti((previous) => ({ ...previous, [key]: [] }));
          setAppliedHeaderMulti((previous) => ({ ...previous, [key]: [] }));
          setPage(1);
        },
      });
    });
    (Object.entries(appliedHeaderRanges) as Array<[SortKey, HeaderRange | undefined]>).forEach(([key, range]) => {
      if (!range?.min && !range?.max) return;
      out.push({
        id: `column-range-${key}`,
        label: `${columnLabels.get(key) || key}: ${rangeText(range?.min, range?.max)}`,
        onRemove: () => {
          setHeaderRanges((previous) => ({ ...previous, [key]: { min: '', max: '' } }));
          setAppliedHeaderRanges((previous) => ({ ...previous, [key]: { min: '', max: '' } }));
          setPage(1);
        },
      });
    });
    return out;
  }, [appliedPeriod, isLeasehold, appliedPropertyType, appliedIncludeNewSale, appliedExcludeExecutiveCondominiums,
    appliedReferenceRadius, appliedSearchMulti, appliedRanges, appliedHeaderMulti, appliedHeaderRanges]);

  const removableFilterCount = useMemo(
    () => activeFilters.filter((filter) => filter.onRemove).length,
    [activeFilters],
  );

  const headerOptions = useMemo(() => {
    const result: Partial<Record<SortKey, string[]>> = {};
    visibleColumns.forEach((col) => {
      result[col.key] = distinctSorted(searchedRows.map((r) => sortValue(r, col.key)));
    });
    result.tenure = ['<= 103 yrs (Leasehold)', '> 103 yrs (999 yrs / Freehold)'];
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedRows, isLeasehold]);

  const toggleSx = {
    '& .MuiToggleButton-root': { px: 1.2, py: 0.4, color: C.textMuted, borderColor: C.border, fontSize: '0.72rem', textTransform: 'none' },
    '& .Mui-selected': { bgcolor: `${C.orange}22 !important`, color: `${C.orange} !important`, borderColor: `${C.orange} !important` },
  } as const;

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    setExportError(null);
    try {
      const valueFor = (row: BenchRow, key: SortKey): string => {
        switch (key) {
          case 'projectName': return row.project.projectName;
          case 'district': return row.project.district == null ? '—' : String(row.project.district);
          case 'region': return row.project.region || '—';
          case 'subtype': return row.project.subtype || '—';
          case 'tenure': return formatTenure(row.project.tenure);
          case 'age': return row.age == null ? '—' : `${row.age}${row.ageEstimated ? '*' : ''}`;
          case 'remainingLease': return isLeasehold ? (row.remainingLease == null ? '—' : String(row.remainingLease)) : 'Freehold';
          case 'completionYear': return row.project.completionYear == null ? '—' : String(row.project.completionYear);
          case 'nearestPriSch': return row.project.nearestPriSch || '—';
          case 'nearestPriSchDistance': return fmtDistance(row.project.nearestPriSchDistance);
          case 'nearestMrt': return row.project.nearestMrt || '—';
          case 'nearestMrtDistance': return fmtDistance(row.project.nearestMrtDistance);
          case 'unitCount': return row.project.unitCount == null ? '—' : row.project.unitCount.toLocaleString();
          case 'txnCount': return row.project.txnCount.toLocaleString();
          case 'avgAreaSqft': return fmt(row.project.avgAreaSqft);
          case 'avgPrice': return fmt(row.project.avgPrice, '$');
          // AA/RY + AA export handling is temporarily disabled with the UI.
          case 'minPsf': return fmt(row.project.minPsf, '$');
          case 'maxPsf': return fmt(row.project.maxPsf, '$');
          case 'avgPsf': return fmt(row.project.avgPsf, '$');
          case 'distanceFromReferenceKm': return row.project.distanceFromReferenceKm == null ? '—' : row.project.distanceFromReferenceKm.toFixed(2);
          case 'adj': return adjustLabel(row.tenureAdj + row.physicalAdj + row.amenitiesAdj, adjust.level);
          case 'personalizedPsf': return fmt(row.personalizedPsf, '$');
          default: return '—';
        }
      };
      // The chip strip above the table is the single source of truth for what
      // the rows were filtered by — the report prints the same list.
      const filterSummary: string[] = [];
      if (appliedPeriod === '1Y' && dateRange) {
        filterSummary.push(`Date range: ${fmtDate(dateRange.start)} – ${fmtDate(dateRange.end)}`);
      }
      activeFilters.forEach((filter) => filterSummary.push(filter.label));
      filterSummary.push(`Adjustment: Level ${adjust.level} · ${methodLabel}`);
      if (adjust.level === 4) {
        const premiumSummary = (label: string, premium: AmenityPremium) => (premium.enabled
          ? `${label}: beyond ${premium.preferredDistanceMeters} m +${premium.mode === 'pct' ? `${premium.value}%` : `$${premium.value} psf`}`
          : `${label}: off`);
        filterSummary.push(premiumSummary('MRT premium', adjust.mrtPremium));
        filterSummary.push(premiumSummary('Pri Sch premium', adjust.priSchPremium));
      }
      const transactionCount = visibleRows.reduce((sum, row) => sum + row.project.txnCount, 0);
      const sortLabel = BENCHMARK_COLUMNS.find((column) => column.key === sortBy)?.label || sortBy;
      await exportPsfPdf({
        userInfo: reportContext?.userInfo || null,
        consumerName: reportContext?.consumerName || undefined,
        reportTitle: 'PERSONALIZED PSF REPORT',
        viewLabel: 'Benchmark vs New Launch',
        subject: '',
        coverSubject: '',
        columns: visibleColumns.map((column) => column.label),
        rows: visibleRows.map((row) => visibleColumns.map((column) => valueFor(row, column.key))),
        filterSummary,
        resultSummary: [
          `Search results: ${visibleRows.length.toLocaleString()} projects`,
          `Transactions represented: ${transactionCount.toLocaleString()}`,
          `Sorted by ${sortLabel}: ${sortDir === 'desc' ? 'descending' : 'ascending'}`,
        ],
        filename: `benchmark-vs-new-launch-${Date.now()}.pdf`,
      });
    } catch (exportFailure: any) {
      setExportError(`PDF export failed: ${exportFailure?.message || 'Unable to generate the report'}`);
    } finally {
      setExportingPdf(false);
    }
  };

  const rangeValue = (key: RangeFieldKey): RangeValue => ({
    min: rangeInputs[`${key}Min`] || '',
    max: rangeInputs[`${key}Max`] || '',
  });
  const setRange = (key: RangeFieldKey, value: RangeValue) => {
    setRangeInputs((previous) => ({ ...previous, [`${key}Min`]: value.min, [`${key}Max`]: value.max }));
    setPage(1);
  };
  const resetRanges = (keys: RangeFieldKey[]) => {
    setRangeInputs((previous) => {
      const next = { ...previous };
      keys.forEach((key) => { delete next[`${key}Min`]; delete next[`${key}Max`]; });
      return next;
    });
  };
  const anyRangeSet = (keys: RangeFieldKey[]) => keys.some((key) => rangeIsSet(rangeValue(key)));
  const renderRangeStat = (key: RangeFieldKey, accent: string, large: boolean | 'xl') => {
    const field = RANGE_FIELDS.find((item) => item.key === key)!;
    return (
      <RangeStat
        key={key}
        label={field.label}
        money={field.money}
        value={rangeValue(key)}
        onChange={(value) => setRange(key, value)}
        presets={RANGE_PRESETS[key]}
        accent={accent}
        c={C}
        emphasis={large}
      />
    );
  };

  const marketActive = period !== '1Y' || propertyType !== 'condo' || excludeExecutiveCondominiums || includeNewSale;
  const budgetActive = anyRangeSet(BUDGET_KEYS);
  const locationActive = Boolean(searchMulti.project.length || searchMulti.district.length || searchMulti.region.length
    || referenceRadius || anyRangeSet(['distanceFromReferenceKm']));
  const amenitiesActive = Boolean(searchMulti.nearestMrt.length || searchMulti.nearestPriSch.length
    || anyRangeSet(['nearestMrtDistance', 'nearestPriSchDistance']));
  const buildingActive = Boolean(searchMulti.subtype.length || anyRangeSet(BUILDING_KEYS));
  // One point per narrowing criterion drafted in the panel — feeds the meter.
  const criteriaCount = activeRangeCount
    + Object.values(searchMulti).filter((values) => values.length > 0).length
    + (referenceRadius ? 1 : 0)
    + (excludeExecutiveCondominiums ? 1 : 0);

  // The tenure toggle sits beside the table, so it applies straight away in
  // both modes rather than waiting for Apply.
  const switchTenure = (value: TenureChoice) => {
    if (value === activeTenure) return;
    setActiveTenure(value);
    setAppliedTenure(value);
    setAdjust(normalizeAdjustmentState(defaultAdjustmentState(value), value));
    // Header filters and explicit sorting are shared by both tenure
    // tabs. Keep them active when switching so the same filtered
    // comparison is shown for Leasehold and Freehold.
    setPage(1);
    setDialog((current) => ({ ...current, open: false }));
  };

  // Classic and Guided share every control; Guided renders them larger.
  const criteriaControls = (large: boolean, extraLarge = false) => {
    const size = extraLarge ? 'xl' : large ? 'lg' : 'md';
    const gap = extraLarge ? 1.25 : large ? 1 : 0.75;
    const rangeGridSx = {
      display: 'grid', gap,
      gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${extraLarge ? 195 : large ? 170 : 136}px), 1fr))`,
    } as const;
    return {
      market: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: extraLarge ? 1.5 : large ? 1.25 : 0.75 }}>
          <PillSelect<Period>
            value={period}
            onChange={(value) => { setPeriod(value); setPage(1); }}
            options={[{ value: '1Y', label: '1 Year' }, { value: '2Y', label: '2 Years' }, { value: '5Y', label: '5 Years' }]}
            accent={QUEST_ACCENTS.market}
            size={size}
            c={C}
          />
          <PillSelect<PropertyTypeFilter>
            value={propertyType}
            onChange={(value) => { setPropertyType(value); setPage(1); }}
            options={[
              { value: 'condo', label: 'Condo', icon: <ApartmentIcon /> },
              { value: 'landed', label: 'Landed', icon: <HouseIcon /> },
              { value: 'all', label: 'All', icon: <AllInclusiveIcon /> },
            ]}
            accent={QUEST_ACCENTS.market}
            size={size}
            c={C}
          />
          <Box sx={{ display: 'flex', gap, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleChip
              label="Exclude E.C"
              icon={<BlockIcon />}
              on={excludeExecutiveCondominiums}
              onToggle={() => { setExcludeExecutiveCondominiums((on) => !on); setPage(1); }}
              accent={QUEST_ACCENTS.market}
              size={size}
              c={C}
            />
            <ToggleChip
              label="Include New Sale"
              icon={<FiberNewIcon />}
              on={includeNewSale}
              onToggle={() => { setIncludeNewSale((on) => !on); setPage(1); }}
              accent={QUEST_ACCENTS.market}
              size={size}
              c={C}
              adornment={(
                <IconButton size="small" onClick={(event) => setNewSaleHelpAnchor(event.currentTarget)} aria-label="About including new sales" sx={{ p: 0.4 }}>
                  <HelpOutlineIcon sx={{ color: C.textMuted, fontSize: large ? 17 : 15 }} />
                </IconButton>
              )}
            />
            <Popover
              open={Boolean(newSaleHelpAnchor)}
              anchorEl={newSaleHelpAnchor}
              onClose={() => setNewSaleHelpAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Typography sx={{ p: 1.25, maxWidth: 300, fontSize: '0.75rem' }}>
                Benchmark uses resale and sub sale transactions by default. Enable this option to include new-sale caveats too.
              </Typography>
            </Popover>
          </Box>
        </Box>
      ),
      budget: (
        <Box sx={large
          ? { display: 'flex', flexDirection: 'column', gap }
          : { ...rangeGridSx, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 190px), 1fr))' }}
        >
          {BUDGET_KEYS.map((key) => renderRangeStat(key, QUEST_ACCENTS.budget, extraLarge ? 'xl' : large))}
        </Box>
      ),
      location: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
            <MultiSelectFilter label="Projects" options={searchOptions.project} value={searchMulti.project} fullWidth size={size}
            onChange={(project) => { setSearchMulti((v) => ({ ...v, project })); setPage(1); }} c={C} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
            <MultiSelectFilter label="Districts" options={searchOptions.district} value={searchMulti.district} fullWidth size={size}
              onChange={(district) => { setSearchMulti((v) => ({ ...v, district })); setPage(1); }} c={C} />
            <MultiSelectFilter label="Regions" options={searchOptions.region} value={searchMulti.region} fullWidth size={size}
              onChange={(region) => { setSearchMulti((v) => ({ ...v, region })); setPage(1); }} c={C} />
          </Box>
          <ReferenceRadiusFilter c={C} resetKey={referenceResetKey} onChange={handleReferenceRadiusChange} emphasis={extraLarge} />
          {renderRangeStat('distanceFromReferenceKm', QUEST_ACCENTS.location, extraLarge ? 'xl' : large)}
        </Box>
      ),
      amenities: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: extraLarge ? 1.5 : large ? 1.25 : 0.75 }}>
          {([
            { key: 'nearestMrt', distanceKey: 'nearestMrtDistance', label: 'Nearest MRT', icon: <TrainIcon /> },
            { key: 'nearestPriSch', distanceKey: 'nearestPriSchDistance', label: 'Nearest Pri Sch', icon: <SchoolIcon /> },
          ] as const).map((amenity) => (
            <Box key={amenity.key} sx={{ display: 'flex', gap: extraLarge ? 1.25 : large ? 1 : 0.75, alignItems: 'flex-start' }}>
              <Box sx={{
                width: extraLarge ? 42 : large ? 34 : 26, height: extraLarge ? 42 : large ? 34 : 26, mt: 0.25, flexShrink: 0,
                borderRadius: extraLarge ? 1.75 : large ? 1.5 : 1.25, display: 'grid', placeItems: 'center',
                bgcolor: `${QUEST_ACCENTS.amenities}22`, color: QUEST_ACCENTS.amenities, '& svg': { fontSize: extraLarge ? 25 : large ? 20 : 16 },
              }}>
                {amenity.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: extraLarge ? 1 : large ? 0.75 : 0.5 }}>
                <MultiSelectFilter label={amenity.label} options={searchOptions[amenity.key]} value={searchMulti[amenity.key]} fullWidth size={size}
                  onChange={(values) => { setSearchMulti((v) => ({ ...v, [amenity.key]: values })); setPage(1); }} c={C} />
                {renderRangeStat(amenity.distanceKey, QUEST_ACCENTS.amenities, extraLarge ? 'xl' : large)}
              </Box>
            </Box>
          ))}
        </Box>
      ),
      building: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
          <MultiSelectFilter label="Types" options={searchOptions.subtype} value={searchMulti.subtype} fullWidth size={size}
            onChange={(subtype) => { setSearchMulti((v) => ({ ...v, subtype })); setPage(1); }} c={C} />
          <Box sx={rangeGridSx}>
            {BUILDING_KEYS.map((key) => renderRangeStat(key, QUEST_ACCENTS.building, extraLarge ? 'xl' : large))}
          </Box>
        </Box>
      ),
    };
  };
  const controls = criteriaControls(queryMode === 'guided');
  const guidedEmphasisControls = queryMode === 'guided' ? criteriaControls(true, true) : controls;

  const modeToggle = (
    <ToggleButtonGroup
      value={queryMode}
      exclusive
      size="small"
      aria-label="Search mode"
      sx={toggleSx}
      onChange={(_, value: QueryMode | null) => { if (value) changeQueryMode(value); }}
    >
      <ToggleButton value="classic">
        <ViewQuiltIcon sx={{ fontSize: 15, mr: 0.5 }} />Classic
      </ToggleButton>
      <ToggleButton value="guided">
        <AutoAwesomeIcon sx={{ fontSize: 15, mr: 0.5 }} />Guided
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const journeySteps: JourneyStep[] = [
    {
      key: 'tenure',
      label: 'Tenure',
      question: 'Which tenure are you benchmarking?',
      highlight: 'tenure',
      hint: 'The resale transactions will match the tenure you pick — it also sets how they are depreciated.',
      icon: <HourglassBottomIcon />,
      accent: QUEST_ACCENTS.tenure,
      answered: true,
      render: ({ next }) => {
        const pick = (value: TenureChoice) => { switchTenure(value); window.setTimeout(next, 280); };
        return (
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <ChoiceTile
              icon={<HourglassBottomIcon />}
              title="Leasehold"
              subtitle="Leases of 103 years or less (typically 99-year). Depreciated by remaining lease on an adjustable curve."
              accent={QUEST_ACCENTS.tenure}
              selected={isLeasehold}
              onClick={() => pick('leasehold')}
              c={C}
            />
            <ChoiceTile
              icon={<AllInclusiveIcon />}
              title="999-year / Freehold"
              subtitle="No lease decay — only the building ages. Land + building split or a market-derived age discount."
              accent={QUEST_ACCENTS.tenure}
              selected={!isLeasehold}
              onClick={() => pick('freehold')}
              c={C}
            />
          </Box>
        );
      },
    },
    {
      key: 'market',
      label: 'Market',
      question: 'Which slice of the market?',
      highlight: 'market',
      hint: 'Pick the time window and property type. Resale and sub sale are in by default.',
      icon: <EventIcon />,
      accent: QUEST_ACCENTS.market,
      answered: marketActive,
      render: () => controls.market,
    },
    {
      key: 'budget',
      label: 'Budget & Size',
      question: "What's the budget and size?",
      highlight: 'budget and size',
      hint: 'Tap a quick pick or type your own range — leave any of them open.',
      icon: <PaidIcon />,
      accent: QUEST_ACCENTS.budget,
      answered: budgetActive,
      badge: 'MOST USED',
      render: () => guidedEmphasisControls.budget,
    },
    {
      key: 'location',
      label: 'Location',
      question: 'Where should we look?',
      highlight: 'Where',
      hint: 'Name projects, districts or regions — or draw a radius around a reference location.',
      icon: <PlaceIcon />,
      accent: QUEST_ACCENTS.location,
      answered: locationActive,
      render: () => guidedEmphasisControls.location,
    },
    {
      key: 'amenities',
      label: 'Amenities',
      question: 'What should be within reach?',
      highlight: 'within reach',
      hint: 'Near the MRT and the right primary school.',
      icon: <ParkIcon />,
      accent: QUEST_ACCENTS.amenities,
      answered: amenitiesActive,
      render: () => guidedEmphasisControls.amenities,
    },
    {
      key: 'building',
      label: 'Building',
      question: 'What kind of building?',
      highlight: 'building',
      hint: 'Type, age, lease left and project scale.',
      icon: <DomainIcon />,
      accent: QUEST_ACCENTS.building,
      answered: buildingActive,
      render: () => guidedEmphasisControls.building,
    },
  ];

  // Classic: the original all-at-once criteria panel with an Apply button.
  const criteriaPanel = (
    <Paper elevation={0} sx={{ bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 1.5 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={filtersOpen}
        onClick={() => setFiltersOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setFiltersOpen((open) => !open); }
        }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75,
          cursor: 'pointer', userSelect: 'none', minWidth: 0,
          borderBottom: filtersOpen ? `1px solid ${C.border}` : 'none',
        }}
      >
        <FilterAltIcon sx={{ fontSize: 16, color: C.orange }} />
        <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          Search Criteria
        </Typography>
        {removableFilterCount > 0 && (
          <Chip
            size="small"
            label={`${removableFilterCount} active`}
            sx={{ bgcolor: `${C.orange}1e`, color: C.orange, fontWeight: 700, fontSize: '0.64rem', height: 18 }}
          />
        )}
        <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'flex' }, mr: 1 }}>
          <PowerMeter count={criteriaCount} c={C} />
        </Box>
        <Box sx={{ ml: { xs: 'auto', sm: 0 }, display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{filtersOpen ? 'Hide' : 'Show'}</Typography>
          <ExpandMoreIcon sx={{
            fontSize: 18, color: C.textMuted,
            transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms',
          }} />
        </Box>
      </Box>
      <Collapse in={filtersOpen} unmountOnExit>
        <Box sx={{ p: 1.1 }}>
          <Box sx={{
            display: 'grid', gap: 1, alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          }}>
            <QuestCard
              step={1}
              icon={<EventIcon />}
              title="Market"
              hint="Time window, property type & sale mix"
              accent={QUEST_ACCENTS.market}
              active={marketActive}
              onReset={() => {
                setPeriod('1Y'); setPropertyType('condo');
                setExcludeExecutiveCondominiums(false); setIncludeNewSale(false); setPage(1);
              }}
              c={C}
            >
              {controls.market}
            </QuestCard>
            <QuestCard
              step={2}
              icon={<PaidIcon />}
              title="Budget & Size"
              hint="Tap a quick pick or type your own range"
              badge="MOST USED"
              accent={QUEST_ACCENTS.budget}
              active={budgetActive}
              onReset={() => resetRanges(BUDGET_KEYS)}
              c={C}
              sx={{ gridColumn: { sm: 'span 2' } }}
            >
              {controls.budget}
            </QuestCard>
            <QuestCard
              step={3}
              icon={<PlaceIcon />}
              title="Location"
              hint="Projects, districts, regions or a radius"
              accent={QUEST_ACCENTS.location}
              active={locationActive}
              onReset={() => {
                setSearchMulti((previous) => ({ ...previous, project: [], district: [], region: [] }));
                setReferenceRadius(null);
                setReferenceResetKey((key) => key + 1);
                resetRanges(['distanceFromReferenceKm']);
              }}
              c={C}
            >
              {controls.location}
            </QuestCard>
            <QuestCard
              step={4}
              icon={<ParkIcon />}
              title="Amenities"
              hint="Near the MRT and the right primary school"
              accent={QUEST_ACCENTS.amenities}
              active={amenitiesActive}
              onReset={() => {
                setSearchMulti((previous) => ({ ...previous, nearestMrt: [], nearestPriSch: [] }));
                resetRanges(['nearestMrtDistance', 'nearestPriSchDistance']);
              }}
              c={C}
            >
              {controls.amenities}
            </QuestCard>
            <QuestCard
              step={5}
              icon={<DomainIcon />}
              title="Building"
              hint="Type, age, lease and project scale"
              accent={QUEST_ACCENTS.building}
              active={buildingActive}
              onReset={() => {
                setSearchMulti((previous) => ({ ...previous, subtype: [] }));
                resetRanges(BUILDING_KEYS);
              }}
              c={C}
            >
              {controls.building}
            </QuestCard>
          </Box>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, pt: 1,
            borderTop: `1px dashed ${C.border}`,
          }}>
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <PowerMeter count={criteriaCount} c={C} />
            </Box>
            <Typography sx={{ color: C.textMuted, fontSize: '0.68rem', display: { xs: 'none', sm: 'block' } }}>
              {criteriaCount === 0
                ? 'No criteria yet — every quest you complete narrows the field.'
                : `${criteriaCount} ${criteriaCount === 1 ? 'criterion' : 'criteria'} locked in. Apply to see the matches.`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto', flexWrap: 'wrap' }}>
              <Button size="small" onClick={clearFilters} sx={{ color: C.textMuted, border: `1px solid ${C.border}`, textTransform: 'none' }}>
                Clear Filters
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={applyFilters}
                startIcon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none', fontWeight: 800, px: 1.75,
                  background: `linear-gradient(135deg, ${C.orange}, ${C.orangeLight})`,
                  boxShadow: `0 4px 14px -6px ${C.orange}`,
                  '&:hover': { background: `linear-gradient(135deg, ${C.orange}, ${C.orange})` },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );

  // The table, chips and adjustments: the whole Classic view, and the
  // final "reveal" section of the Guided journey.
  const renderWorkspace = (journey: JourneyApi | null) => (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
      {/* ── Left: table ── */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden',
        p: isCompactViewport ? 1 : { xs: 1, sm: 2 },
        gap: isCompactViewport ? 0.75 : 1.25,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: C.text, fontSize: '1.05rem' }}>
                Benchmark vs New Launch
              </Typography>
              <Chip
                size="small"
                label={isLeasehold ? 'Leasehold' : 'Freehold'}
                sx={{ bgcolor: `${C.orange}1e`, color: C.orange, fontWeight: 700, fontSize: '0.66rem', height: 20 }}
              />
              <Chip
                size="small"
                label={`${visibleRows.length.toLocaleString()} projects`}
                sx={{ bgcolor: `${C.navy}22`, color: C.textMuted, fontSize: '0.66rem', height: 20 }}
              />
              {summary.avgBase != null && summary.avgPersonalized != null && (
                <Chip
                  size="small"
                  label={`Avg $${summary.avgBase.toLocaleString()} → $${summary.avgPersonalized.toLocaleString()} new-equiv`}
                  sx={{ bgcolor: `${C.yellow}18`, color: C.yellow, fontWeight: 700, fontSize: '0.66rem', height: 20 }}
                />
              )}
            </Box>
            {dateRange && (
              <Typography variant="caption" sx={{ color: C.textMuted }}>
                <CalendarTodayIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
                {fmtDate(dateRange.start)} – {fmtDate(dateRange.end)}
                {' · '}resale market{includeNewSale ? ' + new sales' : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {isMobile && (
              <Button
                size="small"
                startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
                onClick={() => setAdjustmentsOpen(true)}
                sx={{ color: C.orange, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.orange}88`, borderRadius: 1.5, px: 1.2 }}
              >
                Adjustments
              </Button>
            )}
            {modeToggle}
            {journey && (
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                onClick={() => journey.goTo(0)}
                sx={{ color: C.orange, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.orange}88`, borderRadius: 1.5, px: 1.2 }}
              >
                Edit answers
              </Button>
            )}
            <Button
              size="small"
              startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
              onClick={onReset}
              sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
            >
              Start Over
            </Button>
          </Box>
        </Box>

        {!journey && criteriaPanel}

        {/* ── What the rows below were filtered by ── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap',
          px: 0.25, maxHeight: 64, overflowY: 'auto',
        }}>
          <Typography sx={{ color: C.textMuted, fontSize: '0.66rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            Filters applied:
          </Typography>
          {activeFilters.map((filter) => (
            <Chip
              key={filter.id}
              size="small"
              label={filter.label}
              title={filter.label}
              onDelete={filter.onRemove}
              sx={{
                height: 20, maxWidth: 360, fontSize: '0.64rem',
                bgcolor: filter.onRemove ? `${C.orange}14` : `${C.navy}22`,
                color: filter.onRemove ? C.orange : C.textMuted,
                border: `1px solid ${filter.onRemove ? `${C.orange}55` : C.border}`,
                '& .MuiChip-deleteIcon': { fontSize: 13, color: `${C.orange}aa`, '&:hover': { color: C.orange } },
              }}
            />
          ))}
          {removableFilterCount > 0 && (
            <Button
              size="small"
              onClick={clearAllFilters}
              sx={{ color: C.danger, textTransform: 'none', fontSize: '0.66rem', minWidth: 0, px: 0.75, py: 0 }}
            >
              Clear all
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75 }}>
          <ToggleButtonGroup
            value={activeTenure}
            exclusive
            size="small"
            sx={toggleSx}
            onChange={(_, value: TenureChoice | null) => { if (value) switchTenure(value); }}
          >
            <ToggleButton value="leasehold">
              <HourglassBottomIcon sx={{ fontSize: 15, mr: 0.5 }} />Leasehold
            </ToggleButton>
            <ToggleButton value="freehold">
              <AllInclusiveIcon sx={{ fontSize: 15, mr: 0.5 }} />Freehold
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            size="small"
            startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => void handleExportPdf()}
            disabled={exportingPdf || visibleRows.length === 0 || visibleColumns.length === 0}
            sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
          >
            {exportingPdf ? 'Exporting…' : 'Export PDF'}
          </Button>
          <Button
            size="small"
            startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
            onClick={(event) => setColumnsAnchor(event.currentTarget)}
            sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
          >
            Columns
          </Button>
          <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
            <Box sx={{ minWidth: 220, px: 1.5, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Show Columns</Typography>
              <Button size="small" onClick={() => setVisibleColumnKeys(new Set(BENCHMARK_COLUMNS.map((column) => column.key)))}>
                Reset
              </Button>
            </Box>
            <Divider />
            {availableColumns.map((column) => (
              <MenuItem key={column.key} dense onClick={() => setVisibleColumnKeys((previous) => {
                const next = new Set(previous);
                if (next.has(column.key)) next.delete(column.key);
                else next.add(column.key);
                return next;
              })}>
                <Checkbox size="small" checked={visibleColumnKeys.has(column.key)} />
                <ListItemText primary={column.label} primaryTypographyProps={{ fontSize: '0.78rem' }} />
              </MenuItem>
            ))}
          </Menu>
          </Box>
        </Box>

        {(error || exportError) && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>{exportError || error}</Alert>}

        {isMobile && visibleColumns.length > 2 && (
          <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', px: 0.5, pb: 0.25 }}>
            Swipe horizontally to view all columns.
          </Typography>
        )}
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 220, bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 1.5, overflow: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: visibleColumns.reduce((total, column) => total + (tableColumnWidths[column.key] || 44), 0),
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              {visibleColumns.map((column) => <col key={column.key} style={{ width: tableColumnWidths[column.key] || 44 }} />)}
            </colgroup>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col) => {
                  const headerLines = col.headerLines || [col.label];
                  return <TableCell
                    key={col.key}
                    align={col.numeric ? 'right' : 'left'}
                    sx={{
                      bgcolor: C.navyDark,
                      color: col.key === 'personalizedPsf' ? C.yellow : C.headerText,
                      fontWeight: 700, fontSize: isCompactViewport ? '0.68rem' : '0.72rem', whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                      p: isCompactViewport ? '3px 8px' : '6px 8px',
                      width: tableColumnWidths[col.key] || 44,
                      minWidth: tableColumnWidths[col.key] || 44,
                      maxWidth: tableColumnWidths[col.key] || 44,
                      ...(col.key === 'projectName' ? { position: 'sticky', left: 0, zIndex: 3 } : {}),
                      ...(col.key === 'personalizedPsf' ? {
                        // Keep the right-hand value pinned on desktop, but let
                        // it scroll on phones so it cannot cover every middle
                        // column in the narrow viewport.
                        position: isMobile ? 'static' : 'sticky',
                        right: isMobile ? 'auto' : 0,
                        zIndex: isMobile ? 1 : 6,
                        isolation: isMobile ? 'auto' : 'isolate',
                        contain: isMobile ? 'none' : 'paint',
                        width: tableColumnWidths[col.key] || 44,
                        minWidth: tableColumnWidths[col.key] || 44,
                        maxWidth: tableColumnWidths[col.key] || 44,
                        overflow: 'visible', whiteSpace: 'nowrap',
                        boxShadow: isMobile ? 'none' : `-4px 0 8px ${C.navyDark}66`,
                      } : {}),
                      ...(col.key === 'personalizedPsf' ? { overflow: 'visible' } : {}),
                      borderBottom: `2px solid ${col.key === 'personalizedPsf' ? C.yellow : 'rgba(255,255,255,0.15)'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, justifyContent: col.numeric ? 'flex-end' : 'flex-start' }}>
                      <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'center' }}>
                        {headerLines.map((line) => <span key={line}>{line}</span>)}
                      </span>
                      <HeaderMenu
                        filterType={col.filterType}
                        options={headerOptions[col.key] || []}
                        multiValue={headerMulti[col.key] || []}
                        rangeValue={headerRanges[col.key] || { min: '', max: '' }}
                        onMultiChange={(value) => {
                          setHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setAppliedHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setPage(1);
                        }}
                        onRangeChange={(value) => {
                          setHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setAppliedHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setPage(1);
                        }}
                        onSort={(direction) => handleSort(col.key, direction)}
                        sortable={!col.filterDisabled}
                        sortByThisColumn={sortBy === col.key}
                        sortDir={sortDir}
                        defaultSortActive={sortMode === 'default'}
                        filterDisabled={col.filterDisabled}
                        groupThousands={['unitCount', 'avgAreaSqft', 'avgPrice', 'minPsf', 'maxPsf', 'avgPsf', 'adj', 'personalizedPsf'].includes(col.key)}
                        c={C}
                      />
                    </Box>
                  </TableCell>;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={Math.max(1, visibleColumns.length)} sx={{ textAlign: 'center', py: 6, bgcolor: C.surfaceCard }}>
                    <CircularProgress size={28} sx={{ color: C.orange }} />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Math.max(1, visibleColumns.length)} sx={{ textAlign: 'center', py: 6, color: C.textMuted, bgcolor: C.surfaceCard }}>
                    No projects found for this tenure and period
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((r, idx) => {
                  const totalAdj = r.tenureAdj + r.physicalAdj + r.amenitiesAdj;
                  return (
                    <TableRow key={r.project.projectName} hover sx={{
                      bgcolor: idx % 2 === 0 ? C.surfaceCard : C.surface,
                      '&:hover': { bgcolor: `${C.navyLight}44` },
                      // Clip every cell so long values cannot paint into the sticky
                      // Personalized PSF layer while the row is highlighted.
                      '& td': {
                        borderBottom: `1px solid ${C.border}`, px: '8px', color: C.text,
                        py: isCompactViewport ? '2px' : '4px',
                        fontSize: isCompactViewport ? '0.7rem' : '0.73rem',
                        lineHeight: isCompactViewport ? 1.5 : 1.43,
                        whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip',
                      },
                    }}>
                      {visibleColumns.map((column) => {
                        switch (column.key) {
                          case 'projectName':
                            return <TableCell key={column.key} sx={{
                              fontWeight: 700,
                              position: 'sticky', left: 0, zIndex: 5,
                              isolation: 'isolate', contain: 'paint',
                              bgcolor: `${idx % 2 === 0 ? C.surfaceCard : C.surface} !important`,
                              boxShadow: `2px 0 4px ${C.border}`,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              '&:hover': { bgcolor: `${C.navyLight} !important` },
                              'tr:hover &': { bgcolor: `${C.navyLight} !important` },
                            }}>{displayValueFor(r, column.key)}</TableCell>;
                          case 'personalizedPsf':
                            return (
                              <TableCell
                                key={column.key}
                                align="right"
                                onClick={() => openPsfDialog(r)}
                                  sx={{
                                  position: isMobile ? 'static' : 'sticky',
                                  right: isMobile ? 'auto' : 0,
                                  zIndex: isMobile ? 1 : 5,
                                  isolation: isMobile ? 'auto' : 'isolate',
                                  contain: isMobile ? 'none' : 'paint',
                                  width: tableColumnWidths[column.key] || 44,
                                  minWidth: tableColumnWidths[column.key] || 44,
                                  maxWidth: tableColumnWidths[column.key] || 44,
                                  overflow: 'visible', whiteSpace: 'nowrap',
                                  bgcolor: `${idx % 2 === 0 ? C.surfaceCard : C.surface} !important`,
                                  borderLeft: `2px solid ${C.yellow}44`,
                                  boxShadow: isMobile ? 'none' : `-4px 0 8px ${C.navyDark}66`,
                                  cursor: r.personalizedPsf != null ? 'pointer' : 'default',
                                  // Keep row hover visible in the sticky cell, but
                                  // never allow the scrolling cells beneath it to show.
                                  'tr:hover &': { bgcolor: `${C.navyLight} !important` },
                                  '&:hover': r.personalizedPsf != null ? { bgcolor: `${C.navyLight} !important` } : {},
                                  '& > *': { position: 'relative', zIndex: 1 },
                                }}
                              >
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 'none', overflow: 'visible', whiteSpace: 'nowrap' }}>
                                    <Typography sx={{ fontWeight: 800, color: C.yellow, fontSize: isCompactViewport ? '0.72rem' : '0.78rem' }}>
                                      {displayValueFor(r, column.key)}
                                    </Typography>
                                    {r.personalizedPsf != null && <TouchAppIcon sx={{ fontSize: 13, color: `${C.yellow}88` }} />}
                                </Box>
                              </TableCell>
                            );
                          default:
                            return <TableCell key={column.key} align={column.numeric ? 'right' : 'left'}>{displayValueFor(r, column.key)}</TableCell>;
                        }
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, pt: 0.5 }}>
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{
              '& .MuiPaginationItem-root': { color: C.textMuted, borderColor: C.border },
              '& .Mui-selected': { bgcolor: `${C.orange} !important`, color: '#fff !important' },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 84 }}>
            <Select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              sx={{ fontSize: '0.72rem', color: C.text, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
              {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '0.72rem' }}>{n} / pg</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: C.textMuted, fontSize: '0.65rem' }}>
            * age estimated from lease start
          </Typography>
        </Box>
      </Box>

      {/* ── Adjustments: side panel on desktop, bottom drawer on mobile ── */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={adjustmentsOpen}
          onClose={() => setAdjustmentsOpen(false)}
          PaperProps={{
            sx: {
              height: 'min(85dvh, 720px)',
              bgcolor: C.surfaceCard,
              color: C.text,
              borderTop: `1px solid ${C.border}`,
              borderRadius: '14px 14px 0 0',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: `1px solid ${C.border}` }}>
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '0.85rem' }}>Adjustments</Typography>
            <IconButton size="small" onClick={() => setAdjustmentsOpen(false)} sx={{ color: C.textMuted }} aria-label="Close adjustments">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AdjustmentPanel state={adjust} onChange={setAdjust} c={C} amenityProjects={amenityProjects} />
          </Box>
        </Drawer>
      ) : (
        <Box sx={{
          width: 340, flexShrink: 0, bgcolor: C.surfaceCard,
          borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', minHeight: 0,
        }}>
          <AdjustmentPanel state={adjust} onChange={setAdjust} c={C} amenityProjects={amenityProjects} />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {queryMode === 'guided' ? (
        <BenchmarkJourney
          c={C}
          steps={journeySteps}
          projects={projects}
          matchedNames={matchedNames}
          matchCount={visibleRows.length}
          loading={loading}
          criteriaCount={criteriaCount}
          onEnterResults={recordJourney}
          onReset={onReset}
          headerActions={modeToggle}
          renderResults={renderWorkspace}
        />
      ) : renderWorkspace(null)}

      <NewLaunchDialog
        open={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        psfMax={dialog.psf}
        tenureGroup={activeTenure}
        contextProjectName={dialog.projectName}
        explain={dialog.explain}
        unitFilters={dialog.unitFilters}
        reportContext={reportContext}
        c={C}
      />
    </>
  );
}

function adjustLabel(totalAdj: number, level: number): string {
  if (level === 1) return '—';
  if (totalAdj === 0) return '+$0';
  return `+$${totalAdj.toLocaleString()}`;
}
